/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { IconButtonProps } from '@mui/material';
import MuiIconButton from '@mui/material/IconButton';
import React from 'react';

import { Colors } from 'src/common/ui/base/Colors';
import { Spacing } from 'src/common/ui/base/Spacing';
import { BoulderIcon } from 'src/common/ui/components/BoulderIcon';
import { BoulderIconName } from 'src/common/ui/components/icons';

type CircleButtonProps = {
  className?: string;
  color?: IconButtonProps['color'] | 'inverted' | 'transparent-inverted';
  'data-testid'?: string;
  disabled?: boolean;
  icon: BoulderIconName;
  /** Optional caption rendered below the circular button (Figma "with label" variant). */
  label?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  size?: 'small' | 'medium' | 'large';
};

export const CircleButton = ({
  className,
  color = 'primary',
  'data-testid': dataTestId = 'nds-component-circle-button',
  disabled = false,
  icon,
  label,
  onClick,
  size = 'medium',
}: CircleButtonProps) => {
  let iconSize = 24;
  if (size === 'small') {
    iconSize = 16;
  } else if (size === 'large') {
    iconSize = 32;
  }

  const button = (
    <MuiIconButton
      // The visible caption renders as a sibling of the button, so it is not part of the
      // button's accessible name. Mirror the label onto aria-label so screen-reader users
      // focus a named button instead of an icon-only (aria-hidden) one.
      aria-label={label}
      className={className}
      color={color === 'inverted' || color === 'transparent-inverted' ? 'default' : color}
      data-testid={dataTestId}
      disabled={disabled}
      onClick={onClick}
      size={size}
      css={css`
        position: relative;
        overflow: hidden;
        border-radius: 50%;

        ${size === 'small' &&
        css`
          width: ${Spacing.Unit2 * 4}px;
          height: ${Spacing.Unit2 * 4}px;
        `}
        ${size === 'medium' &&
        css`
          width: ${Spacing.Unit2 * 5}px;
          height: ${Spacing.Unit2 * 5}px;
        `}
      ${size === 'large' &&
        css`
          width: ${Spacing.Unit2 * 6}px;
          height: ${Spacing.Unit2 * 6}px;
        `}

      ${color === 'inverted' &&
        css`
          background-color: ${Colors.BlueSpruce};
          color: ${Colors.White};

          &:hover {
            background-color: ${Colors.Blue4};
          }
        `}


      ${color === 'transparent-inverted' &&
        css`
          color: ${Colors.White};
          background-color: rgba(255, 255, 255, 0);

          &:hover {
            background-color: rgba(255, 255, 255, 0.1);
          }
        `}
      `}
    >
      <figure
        css={css`
          display: block;
          margin: 0;
          padding: 0;
          position: relative;
          z-index: 2;

          svg {
            display: block;
          }
        `}
      >
        <BoulderIcon icon={icon} size={iconSize} />
      </figure>
    </MuiIconButton>
  );

  // Icon-only callers must render exactly as before, so only the labeled
  // variant gets wrapped in the centered column.
  if (label === undefined) {
    return button;
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: ${Spacing.Unit1 / 2}px;
      `}
    >
      {button}
      <span
        css={css`
          font-family: 'Nunito Sans', sans-serif;
          font-weight: 600;
          font-size: 12px;
          line-height: 1.45;
          color: ${Colors.CoolGray7};
          text-align: center;
        `}
      >
        {label}
      </span>
    </div>
  );
};
